/**
 * Animated brand footer.
 *
 * Inspired by the LangGraph hero "stroke reveal": a base text layer in a
 * dim color sits behind a transparent layer with an outlined stroke; a
 * radial mask sweeps horizontally so only a vertical band of the stroke
 * is visible at a time, revealing the wordmark like a scanner. A blurred
 * "orb" tracks the mask for an ambient glow.
 *
 * Adapted from the user's reference snippet:
 *  - Switched <style jsx> (Next.js only) to a plain styled-jsx-free CSS-in-JS
 *    block via a local <style> tag with a unique scope (the `.ontoligent-fx`
 *    container class) so it works under Vite + React.
 *  - Pulled colors from our CSS theme variables (--accent / --panel / --border)
 *    so the footer stays in sync with the user's accent color choice.
 *  - Reduced size + padding for footer placement (was a hero); tightened
 *    sweep timing; respects prefers-reduced-motion.
 *  - Added muted nav columns + copyright row in the Langgraph footer style.
 */
import { Link } from "@tanstack/react-router";
import { Github, Mail } from "lucide-react"; 
import { BrandLogo } from "./BrandLogo";
export function BrandFooter() {
  return (
    <footer className="ontoligent-fx relative overflow-hidden border-t border-border bg-panel/40">
      {/* Full-bleed ambient grid (covers the whole footer, not just the wordmark band) */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:linear-gradient(hsl(var(--text)/.4)_1px,transparent_1px),linear-gradient(90deg,hsl(var(--text)/.4)_1px,transparent_1px)] [background-size:48px_48px]" />

      {/* Top wordmark canvas — full-bleed so the sweeping mask + orb cover the whole row */}
      <div className="relative flex w-full items-center justify-center px-6 py-14">
        {/* Top glow */}
        <div className="pointer-events-none absolute -top-12 left-1/2 h-40 w-3/5 -translate-x-1/2 rounded-full bg-accent/10 blur-3xl" />

        <div className="relative z-10 mx-auto flex w-full max-w-3xl flex-col items-center text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/5 px-4 py-1.5 text-[11px] uppercase tracking-[0.25em] text-accent backdrop-blur-md">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
            Ontology-driven intelligence
          </div>
 

          {/* Base wordmark (dim) */}
          <h2 className="select-none text-[14vw] font-black leading-[0.9] tracking-[-0.08em] text-border md:text-[7rem]">
            Ontoligent
          </h2>

          <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted">
            The enterprise intelligence platform for governed decisions —
            ontology-as-code, permission-aware retrieval, auditable agents.
          </p>
        </div>

        {/* Stroke reveal layer */}
        <div className="ontoligent-mask pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <h2 className="ontoligent-stroke select-none text-[14vw] font-black leading-[0.9] tracking-[-0.08em] text-transparent md:text-[7rem]">
            Ontoligent
          </h2>
        </div>

        {/* Sweeping orb */}
        <div className="ontoligent-orb pointer-events-none absolute top-1/2" aria-hidden="true" />
      </div>

      {/* Footer columns */}
      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-2 gap-6 border-t border-border/60 px-6 py-8 text-sm md:grid-cols-5">
        <div className="col-span-2">
          <div className="mb-2">
            <BrandLogo className="h-7 w-auto" alt="Ontoligent" />
          </div>
          <p className="max-w-sm text-xs leading-relaxed text-muted">
            The enterprise intelligence platform for governed decisions.
            Ontology-as-code, hybrid retrieval, auditable agents.
          </p>
          <div className="mt-3 flex gap-2">
            <a className="btn-ghost" href="https://github.com/redpillsoftware7/ontolligence" target="_blank" rel="noopener" aria-label="GitHub">
              <Github size={16} />
            </a>
            <a className="btn-ghost" href="mailto:hello@ontoligent.io" aria-label="Email">
              <Mail size={16} />
            </a>
          </div>
        </div>

        <FooterCol title="Product" links={[
          { to: "/", label: "Dashboard" },
          { to: "/agents", label: "Agents" },
          { to: "/retrieval", label: "Retrieval" },
          { to: "/pipelines", label: "Pipelines" },
          { to: "/packs", label: "Packs" },
        ]} />
        <FooterCol title="Resources" links={[
          { to: "/docs", label: "Read the docs" },
          { to: "/howto", label: "How-to guide" },
          { to: "/use-cases", label: "Use cases & blueprints" },
          { to: "/welcome", label: "Why Ontoligent" },
        ]} />
        <FooterCol title="Workspace" links={[
          { to: "/workspaces", label: "Workspaces" },
          { to: "/users", label: "Users & plan" },
          { to: "/settings", label: "Settings" },
        ]} />
      </div>

      <div className="relative mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-2 border-t border-border/60 px-6 py-4 text-[11px] text-muted md:flex-row">
        <div>© {new Date().getFullYear()} Ontoligent · Built for governed enterprise intelligence</div>
        <div className="flex gap-4">
          <Link to="/welcome" className="hover:text-text">Marketing site</Link>
          <Link to="/docs" className="hover:text-text">Docs</Link>
          <Link to="/use-cases" className="hover:text-text">Use cases</Link>
          <Link to="/legal/privacy-policy" className="hover:text-text">Privacy Policy</Link>
          <Link to="/legal/terms-and-conditions" className="hover:text-text">Terms</Link>
        </div>
      </div>

      {/* Scoped styles. We avoid styled-jsx (Next.js only) and use a plain
         <style> tag scoped via the `.ontoligent-fx` parent class. */}
      <style>{`
        .ontoligent-fx .ontoligent-stroke {
          -webkit-text-stroke: 1.5px hsl(var(--accent));
          filter: drop-shadow(0 0 8px hsl(var(--accent) / 0.45));
        }
        .ontoligent-fx .ontoligent-mask {
          --x: -200px;
          mask-image: radial-gradient(circle 180px at var(--x) 50%,
            #000 0%, rgba(0,0,0,0.7) 45%, transparent 100%);
          -webkit-mask-image: radial-gradient(circle 180px at var(--x) 50%,
            #000 0%, rgba(0,0,0,0.7) 45%, transparent 100%);
          animation: onto-sweep 7s ease-in-out infinite alternate;
        }
        .ontoligent-fx .ontoligent-orb {
          left: 0;
          width: 220px;
          height: 220px;
          border-radius: 9999px;
          background: radial-gradient(circle,
            hsl(var(--accent) / 0.45) 0%,
            hsl(var(--accent) / 0.12) 40%,
            transparent 70%);
          filter: blur(28px);
          transform: translate(-50%, -50%);
          animation: onto-orb 7s ease-in-out infinite alternate;
        }
        @keyframes onto-sweep {
          0%   { --x: -180px; }
          100% { --x: 110%; }
        }
        @keyframes onto-orb {
          0%   { left: -4%; }
          100% { left: 104%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .ontoligent-fx .ontoligent-mask,
          .ontoligent-fx .ontoligent-orb { animation: none; }
          .ontoligent-fx .ontoligent-mask { mask-image: none; -webkit-mask-image: none; }
        }
      `}</style>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: { to: string; label: string }[] }) {
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">{title}</div>
      <ul className="space-y-1.5">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="text-xs text-muted hover:text-text">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
